import { motion, type Variants } from "framer-motion";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  duration?: number;
  staggerChildren?: boolean;
  staggerDelay?: number;
}

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const fadeDown: Variants = {
  hidden: { opacity: 0, y: -40 },
  visible: { opacity: 1, y: 0 },
};

const fadeLeft: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0 },
};

const fadeRight: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0 },
};

const fadeNone: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const directionMap: Record<string, Variants> = {
  up: fadeUp,
  down: fadeDown,
  left: fadeLeft,
  right: fadeRight,
  none: fadeNone,
};

export default function ScrollReveal({
  children,
  className = "",
  delay = 0,
  direction = "up",
  duration = 0.7,
  staggerChildren = false,
  staggerDelay = 0.1,
}: ScrollRevealProps) {
  const variants = directionMap[direction] || fadeUp;

  const containerVariants: Variants = staggerChildren
    ? {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: delay,
            duration,
          },
        },
      }
    : variants;

  const itemVariants = staggerChildren ? variants : undefined;

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px 0px" }}
      variants={containerVariants}
      transition={
        staggerChildren
          ? undefined
          : { duration, delay, ease: [0.25, 0.1, 0.25, 1] }
      }
    >
      {staggerChildren
        ? (Array.isArray(children) ? children : [children]).map((child, i) => (
            <motion.div key={i} variants={itemVariants}>
              {child}
            </motion.div>
          ))
        : children}
    </motion.div>
  );
}

export { fadeUp, fadeDown, fadeLeft, fadeRight, fadeNone };
export type { Variants };